import React, { memo, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { trackEvent } from "../utils/analytics/track";
import { useMeasurementResultDoc } from "../hooks/results/useMeasurementResultDoc";
import { parseTimestampFromRecordingId } from "../utils/results/recordingIdTime";

// Variant views
import CardiopulmonaryResultView from "../components/results/views/CardiopulmonaryResultView";
import SpirometryResultView from "../components/results/views/SpirometryResultView";
import ManualAudioResultView from "../components/results/views/ManualAudioResultView";
import PostExertionResultView from "../components/results/views/PostExertionResultView";
import SixMinuteWalkResultView from "../components/results/views/SixMinuteWalkResultView";
import {
  getUploadFailedMessage,
  getUploadFailedUserMessage,
  isUploadFailedDoc,
} from "../utils/results/isUploadFailedDoc";
import RegionalSupportCodeNotice from "../components/backend/RegionalSupportCodeNotice";
import ResultPageLoadingShell from "../components/pageLoading/ResultPageLoadingShell";
import { getRegionalSupportCode } from "../utils/backend/regionalSupportCodes";
import { RECORDING_MODES } from "../utils/recordingModes";
import {
  getRecordingModeDestination,
  getReviewMetadataPath,
} from "../utils/navigation/recordingModeNav";
import OutstandingTasksCard from "../components/tasks/OutstandingTasksCard";
import { hasOutstandingTaskBoardTasks } from "../utils/tasks/taskBoardSummary";

function resolveVariantFromContext(context) {
  const ctx = (context || "").toLowerCase();

  if (ctx === "spirometry") return "RecordingSpirometry";
  if (ctx === "manual-audio") return "RecordingManualAudio";
  if (ctx === "post-exertion") return "RecordingPostExertion";
  if (ctx === "six-minute-walk") return "RecordingSixMinuteWalk";
  if (ctx === "pulse") return "RecordingPulseScan";
  if (
    ctx === "respiratory" ||
    ctx === "cardiopulmonary" ||
    ctx === "cardiorespiratory"
  ) {
    return "RecordingTidalBreathing";
  }

  return "";
}

function resolveResultsTab({ variant, context, docData }) {
  const docContext =
    (typeof docData?.appMode === "string" && docData.appMode.toLowerCase()) ||
    null;
  const ctx = (context || docContext || "").toLowerCase();

  if (variant === "RecordingSpirometry" || ctx === "spirometry") {
    return "spiro";
  }

  if (variant === "RecordingPulseScan" || ctx === "pulse") {
    return "pulse";
  }

  if (
    variant === "RecordingTidalBreathing" ||
    ctx === "respiratory" ||
    ctx === "cardiopulmonary" ||
    ctx === "cardiorespiratory"
  ) {
    return "cardiopulmonary";
  }

  if (variant === "RecordingManualAudio" || ctx === "manual-audio") {
    return "manual-audio";
  }

  if (variant === "RecordingPostExertion" || ctx === "post-exertion") {
    return "post-exertion";
  }

  if (variant === "RecordingSixMinuteWalk" || ctx === "six-minute-walk") {
    return "six-minute-walk";
  }

  return "";
}

// Choose by context first; fallback to variant
function resolveResultView({ variant, context, docData }) {
  const docContext =
    (typeof docData?.appMode === "string" && docData.appMode.toLowerCase()) ||
    null;

  const ctx = (context || docContext || "").toLowerCase();

  if (variant === "RecordingSpirometry") return SpirometryResultView;
  if (variant === "RecordingManualAudio") return ManualAudioResultView;
  if (variant === "RecordingPostExertion") return PostExertionResultView;
  if (variant === "RecordingSixMinuteWalk") return SixMinuteWalkResultView;

  // Context-based routing (new world)
  if (
    ctx === "pulse" ||
    ctx === "respiratory" ||
    ctx === "six-minute-walk" ||
    ctx === "cardiopulmonary" ||
    ctx === "cardiorespiratory"
  ) {
    return ctx === "six-minute-walk"
      ? SixMinuteWalkResultView
      : CardiopulmonaryResultView;
  }

  // Legacy fallback by variant (old world)
  if (variant === "RecordingPulseScan") return CardiopulmonaryResultView;
  if (variant === "RecordingTidalBreathing") return CardiopulmonaryResultView;

  return null;
}

function computeAgeMs(docId) {
  const ts = parseTimestampFromRecordingId(docId);
  if (!ts) return null;
  return Date.now() - ts.getTime();
}

function resolveRequiredRecordingMode({ variant, context, docData }) {
  if (variant === "RecordingSpirometry") return RECORDING_MODES.SPIROMETRY;
  if (variant === "RecordingPulseScan") return RECORDING_MODES.PULSE;
  if (variant === "RecordingTidalBreathing") return RECORDING_MODES.TIDAL;
  if (variant === "RecordingManualAudio") return RECORDING_MODES.MAUDIO;
  if (variant === "RecordingPostExertion") return RECORDING_MODES.POSTEXERTION;
  if (variant === "RecordingSixMinuteWalk") {
    return RECORDING_MODES.SIX_MINUTE_WALK;
  }

  const docContext =
    (typeof docData?.appMode === "string" && docData.appMode.toLowerCase()) ||
    null;
  const ctx = (context || docContext || "").toLowerCase();

  if (ctx === "pulse") return RECORDING_MODES.PULSE;
  if (ctx === "manual-audio") return RECORDING_MODES.MAUDIO;
  if (ctx === "post-exertion") return RECORDING_MODES.POSTEXERTION;
  if (ctx === "six-minute-walk") return RECORDING_MODES.SIX_MINUTE_WALK;
  if (
    ctx === "respiratory" ||
    ctx === "cardiopulmonary" ||
    ctx === "cardiorespiratory"
  ) {
    return RECORDING_MODES.TIDAL;
  }

  return null;
}

function normalizeLowerString(value) {
  return typeof value === "string" ? value.trim().toLowerCase() : "";
}

function isPulseResultVariant({ variant, context, docData }) {
  const docContext =
    (typeof docData?.appMode === "string" && docData.appMode.toLowerCase()) ||
    "";
  const ctx = normalizeLowerString(context || docContext);

  return variant === "RecordingPulseScan" || ctx === "pulse";
}

function readPulseHeartDxTerminalState({ variant, context, docData }) {
  if (!isPulseResultVariant({ variant, context, docData })) {
    return null;
  }

  if (!docData || typeof docData !== "object") {
    return null;
  }

  const docStatus = normalizeLowerString(docData.status);
  const uploadStatus = normalizeLowerString(docData.upload);
  const isComplete = docStatus === "complete" || uploadStatus === "complete";

  if (!isComplete && !docData.completedAt) {
    return null;
  }

  const nestedHeartDx = docData?.results?.heartdx?.data;
  const processingStatus = normalizeLowerString(
    nestedHeartDx?.processingStatus || docData?.heartdx_status,
  );
  const statusReason =
    typeof nestedHeartDx?.statusReason === "string" &&
    nestedHeartDx.statusReason.trim().length
      ? nestedHeartDx.statusReason.trim()
      : null;

  if (processingStatus === "insufficient_quality") {
    return {
      titleKey: "result.pulse.qualityFailTitle",
      titleDefault: "Low heart-rate signal quality.",
      bodyKey: "result.pulse.qualityFailSubtitle",
      bodyDefault: "Please try again.",
      statusReason,
    };
  }

  if (processingStatus === "not_processed") {
    return {
      titleKey: "result.timeoutTitle",
      titleDefault: "Results not available",
      bodyKey: "result.timeoutBody",
      bodyDefault: "Please try again.",
      statusReason,
    };
  }

  return null;
}

function ResultsLoadingMessage({ t }) {
  return (
    <>
      <div className="w-full h-24 flex flex-col items-center justify-center">
        <img
          src="/pdx_lung.png"
          alt={t("results.loadingAlt", { defaultValue: "Loading..." })}
          loading="eager"
          className="w-auto h-24 opacity-60 animate-breathe"
        />
      </div>
      <p className="subtitle-text">
        {t("result.wait", "Your recording is being processed")}
      </p>
    </>
  );
}

function ResultPage({
  user,
  isPermitted,
  loadingModes,
  recordingAccessError = null,
  taskBoard = null,
  firstRunByVariant = {},
}) {
  const { t, i18n } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const variantParam =
    params.get("variant") || params.get("measurementVariant") || "";
  const docId =
    params.get("docid") ||
    params.get("docId") ||
    params.get("recordingId") ||
    "";
  const contextParam =
    params.get("context") || params.get("appMode") || params.get("mode") || "";
  const uid = user?.uid || null;
  const variant = useMemo(
    () => variantParam || resolveVariantFromContext(contextParam),
    [variantParam, contextParam],
  );

  const { data, exists, loading, error } = useMeasurementResultDoc(
    uid,
    variant,
    docId,
  );

  const returnTab = useMemo(
    () =>
      params.get("tab") ||
      resolveResultsTab({
        variant,
        context: contextParam,
        docData: data,
      }),
    [params, variant, contextParam, data],
  );
  const finishTarget = useMemo(() => {
    if (!returnTab) return "/results";
    return `/results?tab=${encodeURIComponent(returnTab)}`;
  }, [returnTab]);

  useEffect(() => {
    trackEvent("result_view", {
      variant: variant || null,
      docId: docId || null,
      context: contextParam || null,
      lang: i18n.language || null,
    });
  }, [variant, docId, contextParam, i18n.language]);

  const ageMs = useMemo(() => computeAgeMs(docId), [docId]);
  const isWithin5Min = typeof ageMs === "number" ? ageMs < 5 * 60 * 1000 : true;

  const VariantView = useMemo(() => {
    return resolveResultView({
      variant,
      context: contextParam,
      docData: data, // may be null while loading
    });
  }, [variant, contextParam, data]);

  const uploadFailed = useMemo(
    () => !!exists && isUploadFailedDoc(data),
    [exists, data],
  );

  const uploadFailInfo = useMemo(() => getUploadFailedMessage(data), [data]);
  const uploadFailUserMessage = useMemo(
    () => getUploadFailedUserMessage(data),
    [data],
  );
  const pulseTerminalState = useMemo(
    () =>
      readPulseHeartDxTerminalState({
        variant,
        context: contextParam,
        docData: data,
      }),
    [variant, contextParam, data],
  );

  const requiredRecordingMode = useMemo(
    () =>
      resolveRequiredRecordingMode({
        variant,
        context: contextParam,
        docData: data,
      }),
    [variant, contextParam, data],
  );

  const modeAllowed =
    !requiredRecordingMode || isPermitted?.(requiredRecordingMode) !== false;
  const repeatLinkTo = useMemo(() => {
    if (!requiredRecordingMode) {
      return "";
    }

    const nextPath = getRecordingModeDestination(
      requiredRecordingMode,
      firstRunByVariant,
    );

    if (!nextPath || nextPath === "/") {
      return "";
    }

    return requiredRecordingMode === RECORDING_MODES.SIX_MINUTE_WALK
      ? nextPath
      : getReviewMetadataPath(nextPath, requiredRecordingMode);
  }, [firstRunByVariant, requiredRecordingMode]);
  const showOutstandingTasksPrompt = useMemo(
    () =>
      !taskBoard?.loading &&
      !taskBoard?.error &&
      hasOutstandingTaskBoardTasks(taskBoard),
    [taskBoard],
  );
  const outstandingTasksPrompt = showOutstandingTasksPrompt ? (
    <OutstandingTasksCard />
  ) : null;

  // Extract a generic “isReady” signal from the variant view.
  // Each view defines how to know whether it’s ready (HR exists, FEV1 exists, etc).
  const isReady = useMemo(() => {
    if (!VariantView) return false;
    // convention: view has a static method
    return !!exists && VariantView.isReady?.(data, { docId, variant }) === true;
  }, [VariantView, exists, data, docId, variant]);

  const isTimeout = useMemo(() => {
    if (!VariantView) return false;
    const ready = !!exists && VariantView.isReady?.(data, { docId, variant });
    return !!exists && !ready && !isWithin5Min;
  }, [VariantView, exists, data, docId, variant, isWithin5Min]);

  if (loadingModes) {
    return <ResultPageLoadingShell />;
  }

  if (recordingAccessError) {
    return (
      <div className="w-full max-w-lg h-full min-h-0 overflow-y-auto">
        <section className="min-h-full p-4 flex flex-col items-center text-center gap-2">
          <h2 className="subtitle-text">
            {t("regionalBackend.resultAccess.title")}
          </h2>
          <p className="muted-text">
            {recordingAccessError === "fetch_failed"
              ? t("regionalBackend.resultAccess.fetchFailed")
              : t("regionalBackend.resultAccess.notFound")}
          </p>
          <RegionalSupportCodeNotice
            supportCode={getRegionalSupportCode(
              recordingAccessError === "fetch_failed"
                ? "RESULT_ACCESS_FETCH_FAILED"
                : "RESULT_ACCESS_NOT_FOUND",
            )}
          />

          <div className="mt-auto w-full pb-2">
            <button
              type="button"
              className="primary-button w-full"
              onClick={() => navigate(finishTarget)}
            >
              {t("result.finish", "Finish")}
            </button>
          </div>
        </section>
      </div>
    );
  }

  if (error || !variant || !docId || !uid || !modeAllowed) {
    return (
      <div className="w-full max-w-lg h-full min-h-0 overflow-y-auto">
        <section className="min-h-full p-4 flex flex-col items-center text-center gap-2">
          <h2 className="subtitle-text">
            {t("result.couldntLoadRes", "Couldn't load result.")}
          </h2>
          <p className="muted-text">
            {t("result.timeoutBody", "Please try again.")}
          </p>
          <p className="muted-text">{error?.message || "Unknown error"}</p>

          <div className="mt-auto w-full pb-2">
            <button
              type="button"
              className="primary-button w-full"
              onClick={() => navigate(finishTarget)}
            >
              {t("result.finish", "Finish")}
            </button>
          </div>
        </section>
      </div>
    );
  }

  if (!VariantView) {
    return (
      <div className="w-full max-w-lg h-full min-h-0 overflow-y-auto">
        <section className="min-h-full p-4 flex flex-col items-center text-center gap-2">
          <h2 className="subtitle-text">
            {t(
              "result.unsupported",
              "Result view not implemented for this mode",
            )}
          </h2>
          <p className="muted-text">{variant}</p>

          <div className="mt-auto w-full pb-2">
            <button
              type="button"
              className="primary-button w-full"
              onClick={() => navigate(finishTarget)}
            >
              {t("result.finish", "Finish")}
            </button>
          </div>
        </section>
      </div>
    );
  }

  // UPLOAD FAILED (terminal)
  if (!loading && uploadFailed) {
    return (
      <div className="w-full max-w-lg h-full min-h-0 overflow-y-auto">
        <section className="min-h-full p-4 flex flex-col items-center text-center gap-2">
          <h2 className="subtitle-text">
            {t("result.uploadFailedTitle", "Upload failed")}
          </h2>

          <p className="muted-text">
            {t(
              uploadFailUserMessage?.bodyKey || "result.uploadFailedBody",
              uploadFailUserMessage?.bodyDefault ||
                "We couldn't upload your recording. Please check your connection and try again.",
            )}
          </p>

          {uploadFailUserMessage?.hintKey ? (
            <p className="muted-text">
              {t(
                uploadFailUserMessage.hintKey,
                uploadFailUserMessage.hintDefault,
              )}
            </p>
          ) : null}

          {uploadFailInfo?.msg &&
          uploadFailUserMessage?.showTechnicalMessage !== false ? (
            <p className="muted-text">{uploadFailInfo.msg}</p>
          ) : null}

          <div className="mt-auto w-full pb-2">
            <button
              type="button"
              className="primary-button w-full"
              onClick={() => navigate(finishTarget)}
            >
              {t("result.finish", "Finish")}
            </button>
          </div>
        </section>
      </div>
    );
  }

  if (!loading && !isReady && pulseTerminalState) {
    return (
      <div className="w-full max-w-lg h-full min-h-0 overflow-y-auto">
        <section className="min-h-full p-4 flex flex-col items-center text-center gap-2">
          <p className="subtitle-text">
            {t(
              pulseTerminalState.titleKey,
              pulseTerminalState.titleDefault,
            )}
          </p>
          <p className="muted-text">
            {t(
              pulseTerminalState.bodyKey,
              pulseTerminalState.bodyDefault,
            )}
          </p>
          <div className="mt-auto w-full pb-2">
            <button
              type="button"
              className="primary-button w-full"
              onClick={() => navigate(finishTarget)}
            >
              {t("result.finish", "Finish")}
            </button>
          </div>
        </section>
      </div>
    );
  }

  // TIMEOUT (doc exists, but not “ready” after 5 mins)
  if (!loading && isTimeout) {
    return (
      <div className="w-full max-w-lg h-full min-h-0 overflow-y-auto">
        <section className="min-h-full p-4 flex flex-col items-center text-center gap-2">
          <p className="subtitle-text">
            {t("result.timeoutTitle", "Results not available")}
          </p>
          <p className="muted-text">
            {t("result.timeoutBody", "Please try again.")}
          </p>
          <div className="mt-auto w-full pb-2">
            <button
              type="button"
              className="primary-button w-full"
              onClick={() => navigate(finishTarget)}
            >
              {t("result.finish", "Finish")}
            </button>
          </div>
        </section>
      </div>
    );
  }

  // FETCHING the document → shimmer skeleton (a "page loading" state).
  if (loading) {
    return <ResultPageLoadingShell />;
  }

  // WAIT: the document exists but results are still being calculated/processed
  // within the window → keep the animated lung loader.
  if (!isReady) {
    return (
      <div className="w-full max-w-lg h-full p-4 flex flex-col items-center justify-center gap-6 text-center">
        <ResultsLoadingMessage t={t} />
        {outstandingTasksPrompt}
      </div>
    );
  }

  // READY → delegate render to variant
  return (
    <VariantView
      data={data}
      docId={docId}
      variant={variant}
      repeatLinkTo={repeatLinkTo}
      footer={outstandingTasksPrompt}
    />
  );
}

export default memo(ResultPage);
